'use client';
import React, { useEffect, useRef } from 'react';

export default function HeroSection() {
  const svgRef = useRef<SVGSVGElement>(null);

  return (
    <section
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: '#3B4856' }}
    >
      {/* Subtle radial atmosphere */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(137,176,208,0.12) 0%, transparent 70%)',
        }}
      />
      {/* SVG Line Art — full width, behind text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <svg
          ref={svgRef}
          viewBox="0 0 1200 600"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          {/* Main vein — left to center */}
          <path
            className="vein-path"
            d="M 0 300 C 60 280, 100 320, 160 300 C 220 280, 240 260, 300 270 C 360 280, 380 310, 440 300"
            stroke="#89B0D0"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          {/* Capillary branches — upper */}
          <path
            className="vein-path-2"
            d="M 200 290 C 210 260, 220 240, 240 220 C 250 210, 260 215, 270 200 M 240 220 C 255 205, 265 195, 280 185 M 270 200 C 280 190, 295 188, 310 180"
            stroke="#89B0D0"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          {/* Capillary branches — lower */}
          <path
            className="vein-path-2"
            d="M 250 295 C 258 320, 265 340, 280 360 C 288 372, 300 375, 310 390 M 280 360 C 290 375, 305 382, 320 395 M 310 390 C 322 400, 335 402, 348 412"
            stroke="#89B0D0"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          {/* RBC circles cluster — center-left */}
          <circle
            cx="360" cy="240" r="14"
            stroke="#89B0D0" strokeWidth="1.5" fill="none"
            style={{ strokeDasharray: 88, strokeDashoffset: 88, animation: 'drawLine 1.5s ease 1.8s forwards' }}
          />
          <ellipse
            cx="360" cy="240" rx="8" ry="5"
            stroke="#89B0D0" strokeWidth="1" fill="none"
            style={{ strokeDasharray: 48, strokeDashoffset: 48, animation: 'drawLine 1.2s ease 2.1s forwards' }}
          />
          <circle
            cx="400" cy="260" r="14"
            stroke="#89B0D0" strokeWidth="1.5" fill="none"
            style={{ strokeDasharray: 88, strokeDashoffset: 88, animation: 'drawLine 1.5s ease 2.0s forwards' }}
          />
          <ellipse
            cx="400" cy="260" rx="8" ry="5"
            stroke="#89B0D0" strokeWidth="1" fill="none"
            style={{ strokeDasharray: 48, strokeDashoffset: 48, animation: 'drawLine 1.2s ease 2.3s forwards' }}
          />
          <circle
            cx="380" cy="285" r="12"
            stroke="#89B0D0" strokeWidth="1.5" fill="none"
            style={{ strokeDasharray: 75, strokeDashoffset: 75, animation: 'drawLine 1.3s ease 2.2s forwards' }}
          />
          <ellipse
            cx="380" cy="285" rx="7" ry="4"
            stroke="#89B0D0" strokeWidth="1" fill="none"
            style={{ strokeDasharray: 40, strokeDashoffset: 40, animation: 'drawLine 1.1s ease 2.5s forwards' }}
          />
          {/* Connecting line — RBC to DNA */}
          <path
            className="vein-path-2"
            d="M 440 300 C 500 295, 520 290, 560 280"
            stroke="#89B0D0"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          {/* DNA helix — right side */}
          {/* Backbone strand 1 */}
          <path
            className="vein-path-3"
            d="M 560 180 C 570 200, 560 220, 570 240 C 580 260, 570 280, 580 300 C 590 320, 580 340, 590 360 C 600 380, 590 400, 600 420"
            stroke="#89B0D0"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          {/* Backbone strand 2 */}
          <path
            className="vein-path-3"
            d="M 620 180 C 610 200, 620 220, 610 240 C 600 260, 610 280, 600 300 C 590 320, 600 340, 590 360 C 580 380, 590 400, 580 420"
            stroke="#89B0D0"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          {/* Base pairs */}
          {[190, 210, 230, 252, 272, 292, 312, 332, 352, 372, 392, 410]?.map((y, i) => {
            const x1 = 560 + Math.sin(i * 0.52) * 18;
            const x2 = 620 - Math.sin(i * 0.52) * 18;
            return (
              <line
                key={i}
                x1={x1} y1={y} x2={x2} y2={y}
                stroke="#89B0D0"
                strokeWidth="1"
                strokeLinecap="round"
                style={{
                  strokeDasharray: 60,
                  strokeDashoffset: 60,
                  animation: `drawLine 0.6s ease ${1.5 + i * 0.12}s forwards`,
                  opacity: 0.7,
                }}
              />
            );
          })}
          {/* DNA extending right */}
          <path
            className="vein-path-3"
            d="M 600 420 C 660 415, 720 400, 800 390 C 900 378, 1000 372, 1200 360"
            stroke="#89B0D0"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          {/* Thin capillary top-right atmosphere */}
          <path
            className="vein-path-3"
            d="M 700 200 C 750 195, 800 210, 850 200 C 900 190, 950 205, 1000 195 C 1050 185, 1100 200, 1200 190"
            stroke="#89B0D0"
            strokeWidth="0.8"
            strokeLinecap="round"
            opacity="0.4"
          />
        </svg>
      </div>
      {/* Hero Content */}
      <div className="relative z-10 text-center max-w-3xl mx-auto px-6 pt-32 pb-20">
        {/* Eyebrow */}
        <div
          className="hero-cta inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-8"
          style={{
            background: 'rgba(137,176,208,0.15)',
            border: '1px solid rgba(137,176,208,0.3)',
            color: '#89B0D0',
          }}
        >
          <span className="pulse-dot w-1.5 h-1.5 rounded-full" style={{ background: '#C45B6A' }} />
          Specialist Hematology Practice
        </div>

        <h1
          className="hero-headline font-serif text-6xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight mb-8"
          style={{ color: '#F4F7FA' }}
        >
          Where Blood
          <br />
          <span className="italic font-light" style={{ color: '#89B0D0' }}>
            Becomes
          </span>{' '}
          Answers.
        </h1>

        <p
          className="hero-sub text-lg md:text-xl font-light leading-relaxed mb-12 max-w-xl mx-auto"
          style={{ color: 'rgba(244,247,250,0.72)' }}
        >
          A single vial drawn at 7 a.m. becomes a complete narrative — clotting
          cascades, iron kinetics, cell morphology. For internists, oncologists,
          and patients who need answers.
        </p>

        <div className="hero-cta flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#resources"
            className="btn-rose px-8 py-4 rounded-full text-base font-semibold flex items-center gap-2.5"
          >
            Download the Workup Guide
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </a>
          <a
            href="#diagnostics"
            className="btn-outline px-8 py-4 rounded-full text-base font-semibold"
          >
            Explore Diagnostics
          </a>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
        <span
          className="text-xs tracking-widest uppercase"
          style={{ color: 'rgba(244,247,250,0.35)' }}
        >
          Scroll
        </span>
        <div
          className="w-px h-12 relative overflow-hidden"
          style={{ background: 'rgba(137,176,208,0.2)' }}
        >
          <div
            className="absolute top-0 left-0 w-full"
            style={{
              background: '#89B0D0',
              height: '40%',
              animation: 'scrollLine 2s ease-in-out infinite',
            }}
          />
        </div>
      </div>
      <style jsx>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(300%); }
        }
      `}</style>
    </section>
  );
}